import FloatingPanel from "@imminently/decisively-ui/FloatingPanel";
import Popover, { type PopoverHandle } from "@imminently/decisively-ui/Popover";
import { getSelectionText, isSelectionExpanded, useEditorState, useEventEditorSelectors } from "@udecode/plate-core";
import { getSelectionBoundingClientRect } from "@udecode/plate-floating";
import { useEffect, useRef, useState, type PropsWithChildren } from "react";
import { useFocused } from "slate-react";

export const FloatingToolbar = ({
  readOnly = false,
  enabled = true,
  children,
}: { readOnly?: boolean; enabled?: boolean } & PropsWithChildren) => {
  const focusedEditorId = useEventEditorSelectors.focus();
  const editor = useEditorState();
  const focused = useFocused();
  const popoverRef = useRef<PopoverHandle | null>(null);
  const [waitForCollapsedSelection, setWaitForCollapsedSelection] = useState(false);
  const [open, setOpen] = useState(false);

  const selectionExpanded = editor ? isSelectionExpanded(editor) : false;
  const selectionText = editor ? getSelectionText(editor) : "";

  useEffect(() => {
    if (!focused) {
      setWaitForCollapsedSelection(true);
    }

    if (!selectionExpanded) {
      setWaitForCollapsedSelection(false);
    }
  }, [focused, selectionExpanded]);

  useEffect(() => {
    if (readOnly || !enabled || !editor || !selectionExpanded || !selectionText) {
      setOpen(false);
      return;
    }

    const selectionRect = getSelectionBoundingClientRect();
    if (!selectionRect || (selectionRect.width === 0 && selectionRect.height === 0)) {
      setOpen(false);
      return;
    }

    if (!waitForCollapsedSelection) {
      setOpen(true);
    }
  }, [editor, enabled, focusedEditorId, readOnly, selectionExpanded, selectionText, waitForCollapsedSelection]);

  useEffect(() => {
    if (!open || !popoverRef.current) {
      return;
    }

    const virtualReference: any = {
      getBoundingClientRect: getSelectionBoundingClientRect,
    };

    // Floating UI supports virtual references for selection-anchored elements.
    popoverRef.current.refs.setPositionReference?.(virtualReference);
    popoverRef.current.refs.setReference?.(virtualReference);

    popoverRef.current.update();
  }, [open, selectionText, editor?.selection]);

  if (readOnly || !enabled || !open) {
    return null;
  }

  return (
    <Popover
      interactable={true}
      visible={open}
      mode="click"
      placement="bottom"
      offset={12}
      handleRef={popoverRef}
    >
      <Popover.Content handleVisibility={false} asChild>
        <FloatingPanel
          className={"flex flex-row items-center gap-[0.25rem] p-1"}
          onMouseDown={(event) => event.preventDefault()}
        >
          {children}
        </FloatingPanel>
      </Popover.Content>
    </Popover>
  );
};
